import { Meter } from "@base-ui/react/meter";
import { Popover } from "@base-ui/react/popover";
import { RiQuestionLine } from "react-icons/ri";

import { useGetCredits } from "@/api/credits/get-credits";
import { tv } from "@/lib/tailwind-variants";

const HELP_TEXT =
  "当月に消費したクレジットとプランの付与数です。毎月1日にリセットされます。";

const label = tv({
  base: "text-Small leading-tight whitespace-nowrap",
  variants: {
    tone: { ok: "text-gray-600", low: "text-warning", exhausted: "text-error" },
  },
  defaultVariants: { tone: "ok" },
});

// 塗りはハウスの Meter（rate-bar）と統一して accent-green-blue。残り少・使い切りで色を変える。
const indicator = tv({
  base: "h-full w-full flex-1 transition-all",
  variants: {
    tone: {
      ok: "bg-accent-green-blue-500",
      low: "bg-warning-500",
      exhausted: "bg-error-500",
    },
  },
  defaultVariants: { tone: "ok" },
});

interface Props {
  tenantId: string;
}

/** 当月のクレジット残量。全ロールが閲覧でき、ホストには出さない */
export const CreditMeter = ({ tenantId }: Props) => {
  const {
    isHost,
    remaining,
    planCredits,
    month: yearMonth,
    isExhausted,
    isLow,
  } = useGetCredits(tenantId);

  // ホストはクレジットを消費しないため残量を出さない
  if (isHost) {
    return null;
  }

  // 消費数は残量から導く。ゲート（残量 0）とバー満タンが必ず一致し、超過も 100% で頭打ちになる
  const used = Math.min(planCredits, planCredits - remaining);
  const tone = isExhausted ? "exhausted" : isLow ? "low" : "ok";
  const month = yearMonth ? Number(yearMonth.split("-")[1]) : null;

  return (
    <div className="flex items-center gap-2">
      <Meter.Root
        value={used}
        max={planCredits}
        aria-label="今月のクレジット消費"
        className="w-20"
      >
        {/* トラックは gray-100 だと白/gray-50 背景に同化するため 1 段濃い gray-200。 */}
        <Meter.Track className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <Meter.Indicator className={indicator({ tone })} />
        </Meter.Track>
      </Meter.Root>
      <span className={label({ tone })}>
        {month ? `${month}月: ` : ""}
        {used}/{planCredits} クレジット
      </span>
      <Popover.Root>
        <Popover.Trigger
          openOnHover
          delay={300}
          closeDelay={300}
          aria-label="クレジットについてのヘルプを表示"
          className="grid cursor-help place-content-center rounded-full"
        >
          <RiQuestionLine size={16} className="text-gray-400" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner sideOffset={4} side="bottom">
            <Popover.Popup className="max-w-60 rounded-sm bg-gray-700 px-3 py-2 text-Small leading-snug whitespace-pre-line text-white shadow-md">
              {HELP_TEXT}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
